import LocalShippingIcon from '@mui/icons-material/LocalShippingOutlined';
import ShieldIcon from '@mui/icons-material/ShieldOutlined';
import { Chip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { UserRole } from '@fdt/shared';
import { useTranslation } from 'react-i18next';

/**
 * Which of the two roles somebody holds, as a tinted pill.
 *
 * The distinction is the single most consequential fact in the row — an
 * administrator holds the panel and the settings, field staff hold a van and
 * see only their own routes — and as plain text in a column of plain text it
 * was the one thing nobody's eye caught.
 *
 * Tinted rather than filled, and the same shape as `StatusChip` beside it: a
 * row carrying two solid blocks of colour stops reading as status and starts
 * reading as decoration. The icon does the work at a glance; the word is there
 * for everyone who has not yet learned the icon.
 *
 * `secondary` for the administrator is the brand red, which `theme.ts` keeps
 * for the few things that matter. `info` for field staff is the only other
 * colour in the palette that is neither a warning nor a success, so the
 * ordinary case cannot be mistaken for either.
 */
export function RoleChip({ role }: { role: UserRole }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const admin = role === 'admin';
  const colour = admin ? theme.palette.secondary.main : theme.palette.info.main;

  return (
    <Chip
      size="small"
      icon={
        admin ? (
          <ShieldIcon sx={{ fontSize: 15, color: `${colour} !important` }} />
        ) : (
          <LocalShippingIcon sx={{ fontSize: 15, color: `${colour} !important` }} />
        )
      }
      label={t(`roles.${role}`)}
      sx={{
        fontWeight: 600,
        color: colour,
        bgcolor: alpha(colour, theme.palette.mode === 'dark' ? 0.18 : 0.1),
        border: `1px solid ${alpha(colour, 0.32)}`,
        '& .MuiChip-label': { pl: 0.75 },
      }}
    />
  );
}
